import { useEffect, useState, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, Transition } from '@headlessui/react';
import { X, Plus, Edit, Trash2, Search, FolderOpen, Tag, Calendar } from 'lucide-react';
import {
  fetchCategories,
  createCategory,
  updateCategoryById,
  deleteCategoryById,
  clearMessages,
} from '../../../redux/slices/categorySlice';
import toast from 'react-hot-toast';

const Categories = () => {
  const dispatch = useDispatch();
  const { categories, loading, error, success } = useSelector((state) => state.category);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    categoryNumber: '',
    status: 'active',
  });
  const [editingId, setEditingId] = useState(null);

  // Filter states
  const [statusFilter, setStatusFilter] = useState('All');
  const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);

  // Fetch categories on component mount
  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  // Handle success and error messages
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearMessages());
    }
    if (success) {
      dispatch(clearMessages());
    }
  }, [error, success, dispatch]);

  const openAddModal = () => {
    setIsEditMode(false);
    setFormData({
      name: '',
      description: '',
      categoryNumber: '',
      status: 'active',
    });
    setIsModalOpen(true);
  };

  const openEditModal = (category) => {
    setIsEditMode(true);
    setEditingId(category.id);
    setFormData({
      name: category.name,
      description: category.description,
      categoryNumber: category.categoryNumber,
      status: category.status,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setFormData({
      name: '',
      description: '',
      categoryNumber: '',
      status: 'active',
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    if (!formData.categoryNumber.trim()) {
      toast.error('Category number is required');
      return;
    }

    try {
      if (isEditMode) {
        // Update existing category
        await dispatch(
          updateCategoryById({ categoryId: editingId, categoryData: formData }),
        ).unwrap();
        toast.success('Category updated successfully');
      } else {
        // Add new category
        await dispatch(createCategory(formData)).unwrap();
        toast.success('Category added successfully');
      }

      closeModal();
    } catch (error) {
      toast.error(isEditMode ? 'Failed to update category' : 'Failed to add category');
      console.error('Error:', error);
    }
  };

  const handleDelete = async (categoryId) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await dispatch(deleteCategoryById(categoryId)).unwrap();
        toast.success('Category deleted successfully');
      } catch (error) {
        toast.error('Failed to delete category');
        console.error('Error:', error);
      }
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('All');
    setFromDate(new Date().toISOString().split('T')[0]);
    setToDate(new Date().toISOString().split('T')[0]);
  };

  // Handle search
  const handleSearch = () => {
    toast.success('Filters applied');
  };

  // Filter categories based on search term and status
  const filteredCategories = categories.filter(
    (category) => {
      const matchesSearch =
        category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category.categoryNumber.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'All' || category.status === statusFilter.toLowerCase();

      return matchesSearch && matchesStatus;
    }
  );

  return (
    <div className='min-h-screen bg-gray-50 px-4 py-5 md:px-6 md:py-6'>
      <div className='max-w-7xl mx-auto'>
        {/* Header */}
        <div className='mb-4 md:mb-6 flex justify-between items-start'>
          <div>
            <h1 className='text-[18px] md:text-2xl lg:text-3xl font-bold text-gray-900 mb-1 md:mb-2'>
              Categories Management
            </h1>
            <p className='text-[11px] md:text-sm text-gray-600'>
              Manage product categories for your inventory
              {!loading && (
                <span className='ml-2 text-blue-600 font-medium'>
                  • {filteredCategories.length} {filteredCategories.length === 1 ? 'category' : 'categories'}
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Filters Section */}
        <div className='bg-white rounded-2xl shadow-sm border border-gray-200 p-4 md:p-5 mb-4'>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4'>
            {/* Search */}
            <div className='lg:col-span-2'>
              <label className='block text-[11px] md:text-xs font-medium text-gray-700 mb-1.5'>
                Search Category
              </label>
              <div className='relative'>
                <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                <input
                  type='text'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder='Search by name, number...'
                  className='w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-[13px]'
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className='block text-[11px] md:text-xs font-medium text-gray-700 mb-1.5'>
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-[13px]'
              >
                <option value='All'>All Status</option>
                <option value='Active'>Active</option>
                <option value='Disabled'>Disabled</option>
              </select>
            </div>

            {/* From Date */}
            <div>
              <label className='block text-[11px] md:text-xs font-medium text-gray-700 mb-1.5'>
                From Date
              </label>
              <div className='relative'>
                <Calendar className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                <input
                  type='date'
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className='w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-[13px]'
                />
              </div>
            </div>

            {/* To Date */}
            <div>
              <label className='block text-[11px] md:text-xs font-medium text-gray-700 mb-1.5'>
                To Date
              </label>
              <div className='relative'>
                <Calendar className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                <input
                  type='date'
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className='w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-[13px]'
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className='flex gap-2 mt-4'>
            <button
              onClick={handleSearch}
              className='px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-medium text-[13px]'
            >
              Search
            </button>
            <button
              onClick={handleClearFilters}
              className='px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-medium text-[13px]'
            >
              Clear
            </button>
            <button
              onClick={openAddModal}
              className='ml-auto flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-medium text-[13px]'
            >
              <Plus className='w-4 h-4' />
              <span>Add Category</span>
            </button>
          </div>
        </div>

        {/* Desktop Table */}
        <div className='hidden lg:block bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden'>
          <div className='overflow-x-auto'>
            <table className='w-full'>
              <thead className='bg-gray-50 border-b border-gray-200'>
                <tr>
                  <th className='px-4 py-3 text-left text-[11px] font-semibold text-gray-700 uppercase tracking-wider'>
                    Category ID
                  </th>
                  <th className='px-4 py-3 text-left text-[11px] font-semibold text-gray-700 uppercase tracking-wider'>
                    Name
                  </th>
                  <th className='px-4 py-3 text-left text-[11px] font-semibold text-gray-700 uppercase tracking-wider'>
                    Description
                  </th>
                  <th className='px-4 py-3 text-left text-[11px] font-semibold text-gray-700 uppercase tracking-wider'>
                    Category Number
                  </th>
                  <th className='px-4 py-3 text-left text-[11px] font-semibold text-gray-700 uppercase tracking-wider'>
                    Status
                  </th>
                  <th className='px-4 py-3 text-left text-[11px] font-semibold text-gray-700 uppercase tracking-wider'>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-200'>
                {loading ? (
                  <tr>
                    <td colSpan='6' className='px-6 py-12 text-center'>
                      <div className='flex justify-center items-center'>
                        <div className='w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin' />
                      </div>
                    </td>
                  </tr>
                ) : filteredCategories.length === 0 ? (
                  <tr>
                    <td colSpan='6' className='px-6 py-12 text-center text-gray-500 text-sm'>
                      No categories found
                    </td>
                  </tr>
                ) : (
                  filteredCategories.map((category, index) => (
                    <tr key={category.id} className={`hover:bg-blue-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className='px-4 py-4 whitespace-nowrap'>
                        <div className='flex items-center gap-2'>
                          <div className='w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center'>
                            <FolderOpen className='w-4 h-4 text-blue-600' />
                          </div>
                          <span className='text-[11px] text-gray-500 font-mono'>
                            {category.id.substring(0, 8)}
                          </span>
                        </div>
                      </td>
                      <td className='px-4 py-4 whitespace-nowrap'>
                        <div className='text-[13px] font-semibold text-gray-900'>{category.name}</div>
                        <div className='text-[11px] text-gray-500'>#{category.categoryNumber}</div>
                      </td>
                      <td className='px-4 py-4'>
                        <div className='text-[13px] text-gray-600 max-w-xs truncate'>
                          {category.description || <span className='text-gray-400 italic'>No description</span>}
                        </div>
                      </td>
                      <td className='px-4 py-4 whitespace-nowrap'>
                        <div className='inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 rounded-lg'>
                          <Tag className='w-3.5 h-3.5 text-gray-600' />
                          <span className='text-[12px] font-medium text-gray-700'>{category.categoryNumber}</span>
                        </div>
                      </td>
                      <td className='px-4 py-4 whitespace-nowrap'>
                        <span
                          className={`px-3 py-1.5 inline-flex text-[11px] leading-5 font-semibold rounded-full ${category.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                            }`}
                        >
                          {category.status === 'active' ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td className='px-4 py-4 whitespace-nowrap text-sm'>
                        <div className='flex gap-2'>
                          <button
                            onClick={() => openEditModal(category)}
                            className='p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors'
                            title='Edit'
                          >
                            <Edit className='w-4 h-4' />
                          </button>
                          <button
                            onClick={() => handleDelete(category.id)}
                            className='p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors'
                            title='Delete'
                          >
                            <Trash2 className='w-4 h-4' />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile Cards */}
        <div className='lg:hidden space-y-3 md:space-y-4'>
          {loading ? (
            <div className='flex justify-center items-center py-12'>
              <div className='w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin' />
            </div>
          ) : filteredCategories.length === 0 ? (
            <div className='bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center text-gray-500 text-[13px]'>
              No categories found
            </div>
          ) : (
            filteredCategories.map((category) => (
              <div
                key={category.id}
                className='bg-white rounded-2xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow'
              >
                <div className='flex justify-between items-start mb-4'>
                  <div className='flex items-start gap-3 flex-1 min-w-0'>
                    <div className='w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0'>
                      <FolderOpen className='w-5 h-5 text-blue-600' />
                    </div>
                    <div className='flex-1 min-w-0'>
                      <h3 className='text-[13px] md:text-base font-semibold text-gray-900 truncate'>
                        {category.name}
                      </h3>
                      <p className='text-[11px] md:text-sm text-gray-500 mt-0.5'>
                        ID: {category.id.substring(0, 8)}...
                      </p>
                    </div>
                  </div>
                  <span
                    className={`px-2.5 py-1 text-[11px] font-semibold rounded-full flex-shrink-0 ml-2 ${category.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                      }`}
                  >
                    {category.status === 'active' ? 'Active' : 'Disabled'}
                  </span>
                </div>

                <div className='space-y-2.5 mb-4 bg-gray-50 rounded-xl p-3'>
                  <div className='flex justify-between items-center'>
                    <span className='text-[11px] md:text-sm font-medium text-gray-700 flex items-center gap-1.5'>
                      <Tag className='w-3.5 h-3.5' />
                      Category Number
                    </span>
                    <span className='text-[11px] md:text-sm font-semibold text-gray-900'>
                      {category.categoryNumber}
                    </span>
                  </div>
                  <div className='pt-2 border-t border-gray-200'>
                    <span className='text-[11px] md:text-sm font-medium text-gray-700 block mb-1'>
                      Description
                    </span>
                    <span className='text-[11px] md:text-sm text-gray-600'>
                      {category.description || <span className='text-gray-400 italic'>No description</span>}
                    </span>
                  </div>
                </div>

                <div className='flex gap-2 pt-3 border-t border-gray-200'>
                  <button
                    onClick={() => openEditModal(category)}
                    className='flex items-center gap-2 px-3 md:px-4 h-9 text-blue-600 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors flex-1 justify-center font-medium text-[11px] md:text-sm'
                  >
                    <Edit className='w-4 h-4' />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => handleDelete(category.id)}
                    className='flex items-center gap-2 px-3 md:px-4 h-9 text-red-600 bg-red-50 rounded-xl hover:bg-red-100 transition-colors flex-1 justify-center font-medium text-[11px] md:text-sm'
                  >
                    <Trash2 className='w-4 h-4' />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Add/Edit Modal */}
        <Transition appear show={isModalOpen} as={Fragment}>
          <Dialog as='div' className='relative z-50' onClose={closeModal}>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0'
              enterTo='opacity-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'
            >
              <div className='fixed inset-0 bg-black bg-opacity-25' />
            </Transition.Child>

            <div className='fixed inset-0 overflow-y-auto'>
              <div className='flex min-h-full items-center justify-center p-4 text-center'>
                <Transition.Child
                  as={Fragment}
                  enter='ease-out duration-300'
                  enterFrom='opacity-0 scale-95'
                  enterTo='opacity-100 scale-100'
                  leave='ease-in duration-200'
                  leaveFrom='opacity-100 scale-100'
                  leaveTo='opacity-0 scale-95'
                >
                  <Dialog.Panel className='w-full max-w-md transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all'>
                    {/* Modal Header */}
                    <div className='bg-white border-b border-slate-100 px-4 py-3'>
                      <div className='flex items-center gap-3'>
                        <div className='w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center'>
                          <FolderOpen className='w-5 h-5 text-blue-600' />
                        </div>
                        <div className='flex-1'>
                          <Dialog.Title as='h3' className='text-[18px] font-semibold text-slate-900'>
                            {isEditMode ? 'Edit Category' : 'Add New Category'}
                          </Dialog.Title>
                          <p className='text-[11px] text-slate-500'>
                            {isEditMode ? 'Update category details' : 'Fill in category information'}
                          </p>
                        </div>
                        <button
                          onClick={closeModal}
                          className='text-slate-400 hover:text-slate-600 transition-colors'
                        >
                          <X className='w-5 h-5' />
                        </button>
                      </div>
                    </div>

                    {/* Modal Form */}
                    <div className='p-4'>
                      <form onSubmit={handleSubmit} className='space-y-4'>
                        {/* Category Information Section */}
                        <div className='border-b border-slate-100 pb-4'>
                          <h2 className='text-[13px] font-semibold text-slate-900 mb-3 flex items-center gap-2'>
                            <div className='w-8 h-8 bg-blue-50 rounded-xl flex items-center justify-center'>
                              <Tag className='w-4 h-4 text-blue-600' />
                            </div>
                            Category Information
                          </h2>

                          <div className='space-y-3'>
                            {/* Name */}
                            <div>
                              <label className='block text-[11px] font-medium text-slate-600 mb-1.5'>
                                Category Name <span className='text-red-500'>*</span>
                              </label>
                              <input
                                type='text'
                                name='name'
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder='Enter category name'
                                className='w-full h-9 px-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-1 focus:border-blue-500 focus:ring-blue-500 transition-colors text-[13px]'
                                required
                              />
                            </div>

                            {/* Category Number */}
                            <div>
                              <label className='block text-[11px] font-medium text-slate-600 mb-1.5'>
                                Category Number <span className='text-red-500'>*</span>
                              </label>
                              <input
                                type='text'
                                name='categoryNumber'
                                value={formData.categoryNumber}
                                onChange={handleInputChange}
                                placeholder='e.g., CAT-001'
                                className='w-full h-9 px-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-1 focus:border-blue-500 focus:ring-blue-500 transition-colors text-[13px]'
                                required
                              />
                            </div>

                            {/* Description */}
                            <div>
                              <label className='block text-[11px] font-medium text-slate-600 mb-1.5'>
                                Description
                              </label>
                              <textarea
                                name='description'
                                value={formData.description}
                                onChange={handleInputChange}
                                rows={3}
                                placeholder='Enter category description'
                                className='w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-1 focus:border-blue-500 focus:ring-blue-500 transition-colors resize-none text-[13px]'
                              />
                            </div>

                            {/* Status */}
                            <div>
                              <label className='block text-[11px] font-medium text-slate-600 mb-1.5'>
                                Status <span className='text-red-500'>*</span>
                              </label>
                              <select
                                name='status'
                                value={formData.status}
                                onChange={handleInputChange}
                                className='w-full h-9 px-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-1 focus:border-blue-500 focus:ring-blue-500 transition-colors text-[13px] bg-white'
                                required
                              >
                                <option value='active'>Active</option>
                                <option value='disabled'>Disabled</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className='space-y-2'>
                          <button
                            type='submit'
                            className='w-full h-11 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-colors text-[13px] font-medium'
                          >
                            {isEditMode ? 'Update Category' : 'Create Category'}
                          </button>
                          <button
                            type='button'
                            onClick={closeModal}
                            className='w-full h-11 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors text-[13px] font-medium'
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </div>
  );
};

export default Categories;
